import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiGet, apiPost } from "@/lib/api";
import { ChevronUp, ChevronDown } from "lucide-react";
import { hasRecordedMaintenanceFee, stickerPaymentBlockedReason } from "@/lib/stickerEligibility";
import toyyibPayLogo from "@/assets/toyyibpaylogo.png";

function fmtMonth(m) {
  if (!m) return "—";
  const [yr, mo] = m.split("-");
  const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${labels[parseInt(mo, 10) - 1]} ${yr}`;
}

function iterMonths(startYm, endYm) {
  if (!startYm || !endYm || startYm > endYm) return [];
  const [sy, sm] = startYm.split("-").map(Number);
  const [ey, em] = endYm.split("-").map(Number);
  const months = [];
  let yr = sy;
  let mo = sm;
  while (yr < ey || (yr === ey && mo <= em)) {
    months.push(`${yr}-${String(mo).padStart(2, "0")}`);
    mo += 1;
    if (mo > 12) {
      mo = 1;
      yr += 1;
    }
  }
  return months;
}

const PAYMENT_CATEGORIES = [
  { value: "Maintenance Fee", amount: "80", recurring: true, fixed: true },
  { value: "Sticker fee - Car", amount: "10", recurring: false, fixed: true },
  { value: "Sticker fee - Motorcycle", amount: "5", recurring: false, fixed: true },
  { value: "Other", amount: "", recurring: false, fixed: false },
];

function categoryMeta(name) {
  return PAYMENT_CATEGORIES.find(c => c.value === name) || PAYMENT_CATEGORIES[3];
}

function CategorySelect({ value, onChange, multipleMode, testId }) {
  const options = multipleMode
    ? PAYMENT_CATEGORIES.filter(c => c.recurring)
    : PAYMENT_CATEGORIES;
  return (
    <select className="form-input" value={value} onChange={onChange} data-testid={testId}>
      {options.map(c => (
        <option key={c.value} value={c.value}>{c.value}</option>
      ))}
    </select>
  );
}

export default function SubmitPayment() {
  const { user } = useAuth();
  const [resInfo, setResInfo] = useState(null);
  const [mode, setMode] = useState("single");
  const [form, setForm] = useState({
    month: new Date().toISOString().slice(0, 7),
    startMonth: new Date().toISOString().slice(0, 7),
    endMonth: new Date().toISOString().slice(0, 7),
    category: "Maintenance Fee",
    amount: "80",
    payment_date: new Date().toISOString().slice(0, 10),
    remarks: "",
    quantity: 1,
    vehicle_plates: [""],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [existingPayments, setExistingPayments] = useState([]);

  const selectedMonths = useMemo(
    () => (mode === "multiple" ? iterMonths(form.startMonth, form.endMonth) : []),
    [mode, form.startMonth, form.endMonth]
  );

  const meta = categoryMeta(form.category);
  const isSticker = form.category.startsWith("Sticker fee");
  const hasMaintenanceFee = useMemo(
    () => hasRecordedMaintenanceFee(existingPayments),
    [existingPayments]
  );
  const stickerBlockedReason = stickerPaymentBlockedReason(form.category, hasMaintenanceFee);
  const quantity = isSticker ? Math.max(1, Math.min(20, parseInt(form.quantity, 10) || 1)) : 1;
  const amountPerMonth = (parseFloat(form.amount) || 0) * quantity;
  const totalAmount = mode === "multiple" ? amountPerMonth * selectedMonths.length : amountPerMonth;
  const stickerPlates = resizePlates(form.vehicle_plates, quantity).map(sanitizePlate);
  const plateBlock = stickerPlateError(stickerPlates, quantity, isSticker);
  const amountFixed = meta.fixed;
  const monthAvailabilityWarning = useMemo(() => {
    const months = mode === "multiple" ? selectedMonths : (form.month ? [form.month] : []);
    if (months.length === 0 || existingPayments.length === 0) return null;
    const paid = [];
    for (const month of months) {
      const matches = existingPayments.filter(
        p => p.month === month && p.category === form.category && p.status === "approved"
      );
      if (matches.length > 0) paid.push(month);
    }
    if (paid.length > 0) {
      const labels = paid.map(fmtMonth).join(", ");
      return paid.length === 1
        ? `Payment for ${labels} is already paid.`
        : `Payments for ${labels} are already paid.`;
    }
    return null;
  }, [mode, selectedMonths, form.month, form.category, existingPayments]);

  useEffect(() => {
    apiGet("/stats").then(r => setResInfo(r.data)).catch(() => {});
    apiGet("/payments").then(r => setExistingPayments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!meta.recurring && mode === "multiple") {
      setMode("single");
    }
  }, [form.category, meta.recurring, mode]);

  useEffect(() => {
    if (!isSticker) return;
    setForm(f => {
      const next = resizePlates(f.vehicle_plates, quantity);
      if (next.length === (f.vehicle_plates || []).length) return f;
      return { ...f, vehicle_plates: next };
    });
  }, [isSticker, quantity]);

  function update(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function setQuantity(next) {
    const n = Math.max(1, Math.min(20, parseInt(next, 10) || 1));
    setForm(f => ({ ...f, quantity: n, vehicle_plates: resizePlates(f.vehicle_plates, n) }));
  }

  function updateCategory(e) {
    const next = e.target.value;
    const nextMeta = categoryMeta(next);
    setForm(f => ({
      ...f,
      category: next,
      amount: nextMeta.fixed ? nextMeta.amount : f.amount,
      quantity: next.startsWith("Sticker fee") ? (f.quantity || 1) : 1,
      vehicle_plates: next.startsWith("Sticker fee")
        ? resizePlates(f.vehicle_plates, next.startsWith("Sticker fee") ? (f.quantity || 1) : 1)
        : [""],
    }));
    if (!nextMeta.recurring) {
      setMode("single");
    }
  }

  function setPaymentMode(next) {
    if (next === "multiple") {
      setForm(f => ({
        ...f,
        category: "Maintenance Fee",
        amount: "80",
      }));
    }
    setMode(next);
  }

  function monthsToSubmit() {
    return mode === "multiple" ? selectedMonths : [form.month];
  }

  function findBlockedMonths(months, category) {
    const paid = [];
    for (const month of months) {
      const matches = existingPayments.filter(
        p => p.month === month && p.category === category && p.status === "approved"
      );
      if (matches.length > 0) paid.push(month);
    }
    return { paid, pending: [] };
  }

  function validateMonthsAvailable() {
    const months = monthsToSubmit();
    const { paid } = findBlockedMonths(months, form.category);
    if (paid.length > 0) {
      const labels = paid.map(fmtMonth).join(", ");
      return paid.length === 1
        ? `Payment for ${labels} is already paid. Please choose another month.`
        : `Payments for ${labels} are already paid. Please remove those months and try again.`;
    }
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.amount || amountPerMonth <= 0) {
      setError("Please enter a valid amount.");
      return;
    }

    if (mode === "multiple") {
      if (selectedMonths.length === 0) {
        setError("End month must be on or after start month.");
        return;
      }
      if (selectedMonths.length > 24) {
        setError("Maximum 24 months per submission.");
        return;
      }
    }

    const monthBlock = validateMonthsAvailable();
    if (monthBlock) {
      setError(monthBlock);
      return;
    }
    if (stickerBlockedReason) {
      setError(stickerBlockedReason);
      return;
    }

    const plates = isSticker
      ? resizePlates(form.vehicle_plates, quantity).map(sanitizePlate)
      : [];
    const plateCheck = stickerPlateError(plates, quantity, isSticker);
    if (plateCheck) {
      setError(plateCheck);
      return;
    }
    const uniquePlates = [...new Set(plates.filter(Boolean))];

    setLoading(true);
    try {
      const payload = {
        category: form.category,
        amount: amountPerMonth,
        payment_date: form.payment_date,
        remarks: form.remarks,
        quantity,
      };
      if (isSticker) payload.vehicle_plates = uniquePlates;
      if (mode === "single") {
        payload.month = form.month;
      } else {
        payload.months = selectedMonths;
      }

      const res = await apiPost("/payments/toyyibpay/initiate", payload);
      window.location.href = res.data.payment_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to submit payment. Please try again.");
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="res-summary-cards" data-testid="submit-info-cards">
        <div className="res-card">
          <div className="rc-label">My House No.</div>
          <div className="rc-val" style={{ color: "var(--brand-dark)", fontFamily: "var(--mono)", fontSize: 18 }}>
            {user?.unit || "—"}
          </div>
        </div>
        {user?.street_number && (
          <div className="res-card">
            <div className="rc-label">Street</div>
            <div className="rc-val" style={{ fontSize: 13, color: "var(--text2)" }}>
              {user.street_number}
            </div>
          </div>
        )}
        <div className="res-card">
          <div className="rc-label">Last Payment</div>
          <div className="rc-val" style={{ fontSize: 15 }}>
            {resInfo?.last_payment ? `${fmtMonth(resInfo.last_payment.month)} ✓` : "No payments yet"}
          </div>
        </div>
        <div className="res-card">
          <div className="rc-label">Account Status</div>
          <div className="rc-val" style={{ fontSize: 14 }}>
            <span className={`badge ${user?.status || "pending"}`}>{capitalize(user?.status)}</span>
          </div>
        </div>
      </div>

      <div className="section-panel" data-testid="submit-payment-form">
        <div className="section-head">
          <div className="section-title">Submit Payment</div>
        </div>
        <div style={{ padding: "20px 20px" }}>
          {error && <div className="auth-error" data-testid="submit-error">{error}</div>}
          {success && <div className="auth-success" data-testid="submit-success">{success}</div>}

          <div style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ marginBottom: 8, display: "block" }}>Payment Method</label>
            <div className="payment-gateway-logo" data-testid="toyyibpay-logo">
              <img src={toyyibPayLogo} alt="toyyibPay" />
            </div>
            <div className="payment-notice-info" data-testid="gateway-notice" style={{ marginTop: 10 }}>
              You will be redirected to toyyibPay to complete your payment securely via Online Banking (FPX) or Card.
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ marginBottom: 8, display: "block" }}>Submission Type</label>
            <div style={{ display: "flex", gap: 8 }} data-testid="payment-mode-toggle">
              <button
                type="button"
                className={`btn ${mode === "single" ? "btn-primary" : ""}`}
                onClick={() => setPaymentMode("single")}
                data-testid="mode-single"
              >
                Single Month
              </button>
              <button
                type="button"
                className={`btn ${mode === "multiple" ? "btn-primary" : ""}`}
                onClick={() => setPaymentMode("multiple")}
                disabled={!meta.recurring}
                title={!meta.recurring ? "One-time fees use single submission only" : undefined}
                data-testid="mode-multiple"
              >
                Multiple Months
              </button>
            </div>
            {mode === "multiple" && (
              <div className="payment-notice-info" data-testid="multiple-months-gateway-notice">
                A single online payment will be made for the total amount, and one payment record will be created for each month after successful payment.
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {stickerBlockedReason && (
              <div className="payment-notice-warn" data-testid="sticker-requires-maintenance-notice" style={{ marginBottom: 16 }}>
                {stickerBlockedReason}
              </div>
            )}
            {monthAvailabilityWarning && (
              <div className="payment-notice-warn" data-testid="month-already-paid-notice" style={{ marginBottom: 16 }}>
                {monthAvailabilityWarning} Submission for this month is blocked.
              </div>
            )}
            {mode === "single" ? (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Month *</label>
                  <input
                    type="month"
                    className="form-input"
                    value={form.month}
                    onChange={update("month")}
                    required
                    data-testid="payment-month-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <CategorySelect value={form.category} onChange={updateCategory} testId="payment-category-select" />
                </div>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">From Month *</label>
                    <input
                      type="month"
                      className="form-input"
                      value={form.startMonth}
                      onChange={update("startMonth")}
                      required
                      data-testid="payment-start-month-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">To Month *</label>
                    <input
                      type="month"
                      className="form-input"
                      value={form.endMonth}
                      min={form.startMonth}
                      onChange={update("endMonth")}
                      required
                      data-testid="payment-end-month-input"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <CategorySelect
                    value={form.category}
                    onChange={updateCategory}
                    multipleMode
                    testId="payment-category-select-multi"
                  />
                </div>
                {selectedMonths.length > 0 && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: "12px 14px",
                      background: "var(--brand-light)",
                      border: "1px solid var(--brand-border)",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    data-testid="payment-months-preview"
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6, color: "var(--brand-dark)" }}>
                      {selectedMonths.length} payment{selectedMonths.length === 1 ? "" : "s"} will be created
                    </div>
                    <div style={{ color: "var(--text2)", lineHeight: 1.6 }}>
                      {selectedMonths.map(fmtMonth).join(" · ")}
                    </div>
                  </div>
                )}
              </>
            )}

            {isSticker ? (
              <>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Quantity *</label>
                    <div className="qty-stepper">
                      <input
                        type="number"
                        className="form-input"
                        min="1"
                        max="20"
                        step="1"
                        value={form.quantity}
                        onChange={e => setQuantity(e.target.value)}
                        required
                        data-testid="payment-quantity-input"
                      />
                      <div className="qty-stepper-btns">
                        <button
                          type="button"
                          className="qty-step-btn"
                          onClick={() => setQuantity(quantity + 1)}
                          disabled={quantity >= 20}
                          aria-label="Increase quantity"
                          data-testid="payment-quantity-up"
                        >
                          <ChevronUp size={13} />
                        </button>
                        <button
                          type="button"
                          className="qty-step-btn"
                          onClick={() => setQuantity(quantity - 1)}
                          disabled={quantity <= 1}
                          aria-label="Decrease quantity"
                          data-testid="payment-quantity-down"
                        >
                          <ChevronDown size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Each Amount (RM) *</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="80.00"
                      min="0"
                      step="0.01"
                      value={form.amount}
                      onChange={update("amount")}
                      readOnly={amountFixed}
                      required
                      data-testid="payment-amount-input"
                      style={amountFixed ? { background: "var(--bg)", color: "var(--text2)" } : undefined}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Date *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={form.payment_date}
                      onChange={update("payment_date")}
                      required
                      data-testid="payment-date-input"
                    />
                  </div>
                </div>
                <div className="sticker-total-banner" data-testid="payment-sticker-total">
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text2)", marginBottom: 2 }}>
                      Total Amount
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text2)" }}>
                      {quantity} × RM {Number(form.amount || 0).toFixed(2)} each
                    </div>
                  </div>
                  <div className="mono" style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>
                    RM {amountPerMonth.toFixed(2)}
                  </div>
                </div>
              </>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    {mode === "multiple" ? "Amount per Month (RM) *" : "Amount (RM) *"}
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="80.00"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={update("amount")}
                    readOnly={amountFixed}
                    required
                    data-testid="payment-amount-input"
                    style={amountFixed ? { background: "var(--bg)", color: "var(--text2)" } : undefined}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.payment_date}
                    onChange={update("payment_date")}
                    required
                    data-testid="payment-date-input"
                  />
                </div>
              </div>
            )}

            {mode === "multiple" && selectedMonths.length > 0 && (
              <div className="form-group" data-testid="payment-total-amount">
                <label className="form-label">Total Amount</label>
                <div
                  className="form-input"
                  style={{
                    background: "var(--bg)",
                    fontWeight: 600,
                    fontFamily: "var(--mono)",
                    color: "var(--brand-dark)",
                  }}
                >
                  RM {totalAmount.toFixed(2)}
                  <span style={{ fontWeight: 400, color: "var(--text2)", marginLeft: 8, fontFamily: "var(--font)" }}>
                    ({selectedMonths.length} × RM {amountPerMonth.toFixed(2)})
                  </span>
                </div>
              </div>
            )}

            {isSticker && (
              <div
                className="form-group"
                data-testid="vehicle-plate-fields"
                style={{
                  padding: "14px 14px 6px",
                  background: "var(--brand-light)",
                  border: "1px solid var(--brand-border)",
                  borderRadius: 6,
                }}
              >
                <div className="form-label" style={{ marginBottom: 8 }}>
                  {quantity === 1
                    ? "Vehicle Plate Number *"
                    : `Vehicle Plate Numbers * (${quantity} stickers — one plate per sticker)`}
                </div>
                {quantity > 1 && (
                  <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 10 }}>
                    Enter a unique plate number for each sticker. Letters and numbers only, no spaces. Each plate is saved as its own registered vehicle record.
                  </div>
                )}
                {quantity === 1 && (
                  <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 10 }}>
                    Letters and numbers only. Spaces and special characters are not allowed.
                  </div>
                )}
                {plateBlock && (
                  <div className="payment-notice-warn" data-testid="payment-plate-required-notice" style={{ marginBottom: 10 }}>
                    {plateBlock}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: quantity > 1 ? "repeat(auto-fit, minmax(180px, 1fr))" : "1fr", gap: 10 }}>
                  {resizePlates(form.vehicle_plates, quantity).map((plate, idx) => {
                    const fieldError = plateFieldError(plate);
                    return (
                    <div key={idx} className="form-group" style={{ marginBottom: 10 }}>
                      <label className="form-label">
                        {quantity === 1 ? "Plate number *" : `Vehicle ${idx + 1} *`}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. WXY1234"
                        value={plate}
                        maxLength={15}
                        required
                        minLength={1}
                        pattern="[A-Za-z0-9]+"
                        title="Enter a vehicle plate number using letters and numbers only."
                        inputMode="text"
                        autoComplete="off"
                        aria-invalid={fieldError ? "true" : "false"}
                        onChange={e => {
                          const value = sanitizePlateInput(e.target.value);
                          setForm(f => {
                            const next = resizePlates(f.vehicle_plates, quantity);
                            next[idx] = value;
                            return { ...f, vehicle_plates: next };
                          });
                        }}
                        data-testid={`payment-plate-input-${idx}`}
                        style={fieldError ? { borderColor: "var(--danger)", background: "#fff" } : undefined}
                      />
                      {fieldError && (
                        <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 4 }} data-testid={`payment-plate-error-${idx}`}>
                          {fieldError}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Remarks (optional)</label>
              <textarea
                className="form-input"
                rows={2}
                placeholder="Any notes for the admin..."
                value={form.remarks}
                onChange={update("remarks")}
                data-testid="payment-remarks-input"
              />
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !!monthAvailabilityWarning || !!stickerBlockedReason || !!plateBlock}
              data-testid="submit-payment-btn"
            >
              {loading
                ? "Redirecting to toyyibPay..."
                : `Pay RM ${totalAmount.toFixed(2)} Online`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function resizePlates(plates, count) {
  const next = [...(plates || [])];
  while (next.length < count) next.push("");
  return next.slice(0, count);
}

function sanitizePlateInput(raw) {
  return String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function sanitizePlate(raw) {
  return sanitizePlateInput(raw);
}

function plateFieldError(raw) {
  const plate = sanitizePlate(raw);
  if (!plate) return "Vehicle plate number is required.";
  if (!/^[A-Z0-9]+$/.test(plate)) return "Use letters and numbers only. Spaces are not allowed.";
  return "";
}

function stickerPlateError(plates, quantity, isSticker) {
  if (!isSticker) return null;
  if (plates.length !== quantity || plates.some(plateFieldError)) {
    return quantity === 1
      ? "Please enter a valid vehicle plate number before paying."
      : `Please enter a valid plate number for all ${quantity} vehicles before paying.`;
  }
  const unique = new Set(plates.filter(Boolean));
  if (unique.size !== quantity) return "Each sticker needs a different vehicle plate number.";
  return null;
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}
